// The job status-confirm popup's email editor, as plain decisions (package "popups", owner 2026-10-03:
// every outbound email opens an editor first). No React and no imports, so the package test
// (server/src/scripts/testPopups.ts) runs these very functions against the real server.
//
// The poll (GET /jobs/status-confirm/mine, every 60 s) brings each due row's draft — From / To / Cc /
// Subject / Body as Confirm would send it now — and `sig`, a fingerprint of the job state behind it
// (status, finish date, recipients; the "Updated by" clock pinned). The fields are filled from one
// draft, and Confirm sends THAT draft's sig: the server refuses (409) once the job has moved on, so an
// edited copy of an older state of the job can never reach the client.

export interface JcDraft { from: string; to: string[]; cc: string[]; subject: string; bodyHtml: string; sig: string }
/** The row and the draft the fields were filled from (sig '' = the row had no draft then). */
export interface JcSeeded { id: string; sig: string }

/**
 * What the editor does after a poll, for the row at the front of the queue:
 *   seed    — fill the fields from this draft, edits and warnings cleared (`changed`: tell the operator
 *             the job changed, so the email shown is not the one they were reading);
 *   clear   — a different row (or none) is in front and it has no draft: nothing carries over;
 *   refresh — the same job state with untouched fields: take the new copy (only the "Updated by"
 *             stamp moves);
 *   stale   — the job moved while the fields hold edits: block Confirm and offer to reload the email
 *             (the edits are discarded only once the operator has been told);
 *   none    — keep what is shown.
 */
export type JcStep = { kind: 'seed'; changed: boolean } | { kind: 'clear' } | { kind: 'refresh' } | { kind: 'stale' } | { kind: 'none' };

export function jcDraftStep(seeded: JcSeeded, curId: string, draft: JcDraft | undefined, dirty: boolean, stale: boolean): JcStep {
  // Re-check, Revert, a send, or the row leaving the queue: a row coming (back) to the front starts
  // from its own current draft — edits made before never come back with it.
  if (seeded.id !== curId) return draft ? { kind: 'seed', changed: false } : { kind: 'clear' };
  if (!draft) return { kind: 'none' };   // Confirm stays disabled while there is no draft
  if (draft.sig === seeded.sig) return dirty || stale ? { kind: 'none' } : { kind: 'refresh' };
  if (dirty || stale) return { kind: 'stale' };
  return { kind: 'seed', changed: !!seeded.sig };
}

/** Confirm (and "Status Only") can only send a draft that is loaded and still describes the job. */
export function jcCanSend(seeded: JcSeeded, draft: JcDraft | undefined, stale: boolean): boolean {
  return !!draft && !stale && !!seeded.sig && draft.sig === seeded.sig;
}

/**
 * What Confirm posts, or the i18n key of what is wrong. `to` / `cc` are the parsed address lists.
 * The body goes only once the operator has edited it: untouched, the server renders the email fresh at
 * Confirm, byte for byte the one-click Confirm's ("Updated by … <the moment of Confirm>"); edited, it
 * goes exactly as edited.
 */
export function jcConfirmPayload(seeded: JcSeeded, draft: JcDraft | undefined, stale: boolean,
  f: { to: string[]; cc: string[]; subject: string }, body: { edited: boolean; html: string }):
  { error: string } | { to: string[]; cc: string[]; subject: string; draftSig: string; bodyHtml?: string } {
  if (!draft || !seeded.sig) return { error: 'pop_jc_no_draft' };
  if (!jcCanSend(seeded, draft, stale)) return { error: 'pop_jc_changed' };
  if (!f.to.length) return { error: 'pop_need_to' };
  if (!f.subject.trim()) return { error: 'pop_need_subject' };
  if (body.edited && !body.html.trim()) return { error: 'pop_need_body' };
  return { to: f.to, cc: f.cc, subject: f.subject, draftSig: seeded.sig, ...(body.edited ? { bodyHtml: body.html } : {}) };
}
